import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
    useCatalogImportQuery,
    useUndoCatalogImportMutation,
} from '@/api/queries';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { CatalogImportRecord } from '@/api/models';
import { useCatalogImport } from '@/pages/catalogImport/store';
import { useIsNarrowViewport } from '@/utils';
import { showUndoneToast } from './components';
import { undoFailureOf, undoneMessageOf, type UndoFailure } from './importUndo';

export type UseResultStepOptions = {
    /**
     * Where the step starts over, the branch kept: after Import another file
     * and after an undo. Branch in the wizard.
     */
    anotherFilePath: string;
    /** Where Next step goes: Done in the wizard. */
    nextPath: string;
};

export type UseResultStepResult = {
    /**
     * The completed import: the record as the server reads it back, and the
     * import's own answer until that read lands. `null` before an import.
     */
    record: CatalogImportRecord | null;
    /** Drops the import, keeps the branch, and goes to `anotherFilePath`. */
    onImportAnother: () => void;
    /** Goes on to `nextPath`, the import kept. */
    onNext: () => void;
    /** A phone (below `sm`): the figures stack. */
    isNarrow: boolean;
    isUndoOpen: boolean;
    /** Undo this import: asks first. */
    onUndo: () => void;
    /** Keeps the import: closes the confirm and forgets a failure; ignored mid-call. */
    onCancelUndo: () => void;
    /** Confirm (and Try again after a failure): undoes the import. */
    onConfirmUndo: () => void;
    isUndoing: boolean;
    /** Why the last undo did not go through, shown in the confirm. */
    undoFailure: UndoFailure | null;
};

/**
 * The Result step: the import the Products step stored, read back from the
 * server (`GET catalog-imports/{id}`) so the figures on screen are the ones
 * it keeps. The store's record is the import's own answer, in the same
 * shape and from the same server, so it shows until the read lands and stays
 * if the read fails.
 *
 * - **Import another file** drops the file, its rows, its decisions and the
 *   record (`resetImport`) but keeps the branch, which the Branch step then
 *   shows chosen, and goes there. Nothing is left that the leave guard would
 *   ask about.
 * - **Next step** goes on (Done, or the branch list in-shell) and then
 *   drops the finished import as Import another file does, keeping the
 *   branch: the next file starts on an empty Upload, never on this one and
 *   its record. Browser Back to Result then finds no record and redirects.
 * - **Undo** asks once, then undoes the import on the server
 *   (`useUndoCatalogImportMutation`, which invalidates the import history,
 *   so Done no longer counts it). Done, it toasts what went and what stayed
 *   (`kept[]`), drops the import as Import another file does and goes to
 *   `anotherFilePath`. A refusal (or no answer) stays in the confirm and
 *   leaves the Result as it was. The import is gone on the server once the
 *   answer comes, so the store drops it even when the step has been left
 *   meanwhile; only the navigation needs the step still open.
 */
export const useResultStep = ({
    anotherFilePath,
    nextPath,
}: UseResultStepOptions): UseResultStepResult => {
    const { t } = useTranslation('pages');
    const navigate = useNavigate();
    const { state, actions } = useCatalogImport();
    const isNarrow = useIsNarrowViewport();
    const stored = state.record;
    const { data: readBack } = useCatalogImportQuery(
        stored?.id ?? '',
        stored !== null,
    );
    const {
        mutateAsync: requestUndo,
        isPending: isUndoing,
        reset: resetUndo,
    } = useUndoCatalogImportMutation();
    const [isUndoOpen, setIsUndoOpen] = React.useState(false);
    const [undoFailure, setUndoFailure] = React.useState<UndoFailure | null>(
        null,
    );

    const isMounted = React.useRef(true);
    React.useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    const onImportAnother = React.useCallback(() => {
        navigate(anotherFilePath);
        actions.resetImport();
    }, [actions, anotherFilePath, navigate]);

    // Navigate first, as Import another file does, so this step never
    // renders its own redirect for the emptied store.
    const onNext = React.useCallback(() => {
        navigate(nextPath);
        actions.resetImport();
    }, [actions, navigate, nextPath]);

    const onUndo = React.useCallback(() => {
        setUndoFailure(null);
        setIsUndoOpen(true);
    }, []);

    const onCancelUndo = React.useCallback(() => {
        if (isUndoing) {
            return;
        }
        resetUndo();
        setUndoFailure(null);
        setIsUndoOpen(false);
    }, [isUndoing, resetUndo]);

    const importId = stored?.id ?? null;
    const onConfirmUndo = React.useCallback(() => {
        if (importId === null || isUndoing) {
            return;
        }
        setUndoFailure(null);

        void (async () => {
            try {
                const undone = await requestUndo(importId);
                showUndoneToast(undoneMessageOf(t, undone));
                if (isMounted.current) {
                    navigate(anotherFilePath);
                }
                actions.resetImport();
            } catch (error) {
                if (isMounted.current) {
                    setUndoFailure(undoFailureOf(error as ErrorResponse));
                }
            }
        })();
    }, [
        actions,
        anotherFilePath,
        importId,
        isUndoing,
        navigate,
        requestUndo,
        t,
    ]);

    return {
        record: stored === null ? null : (readBack ?? stored),
        onImportAnother,
        onNext,
        isNarrow,
        isUndoOpen,
        onUndo,
        onCancelUndo,
        onConfirmUndo,
        isUndoing,
        undoFailure,
    };
};
